// Pure operational logic for the staff orders board.
//
// Nothing here touches React, Inertia, or i18n: actions name themselves with a
// translation key so the board and the drawer can render the same flow without
// this module knowing what a locale is.

export type BoardOrder = {
  id: number
  status: string
  serviceType: string
  station: string
  stationLabel: string
  placedAt: string
}

export type Urgency = "normal" | "warn" | "late"

export type OrderFlowAction = {
  status: string
  labelKey: string
  variant: "default" | "destructive" | "outline"
  requiresEta?: boolean
}

export type StationOption = {
  value: string
  label: string
}

const ACTION_I18N_BASE = "ui.staff.orders.actions"

/**
 * How long an order may sit in a given status before the board escalates it.
 *
 * Preparation gets a long leash — cooking takes time. Everything on either side
 * of it is a queue, and a queue that stops moving is the whole reason this
 * screen exists. A plate sitting under the lamp goes stale fastest of all.
 *
 * These are operational judgment calls, not measurements. Tune them per venue.
 */
export const URGENCY_THRESHOLDS: Record<string, { warn: number; late: number }> = {
  pending: { warn: 5, late: 10 },
  in_progress: { warn: 15, late: 25 },
  ready: { warn: 5, late: 10 },
  ready_for_pickup: { warn: 5, late: 10 },
}

const MILLISECONDS_PER_MINUTE = 60_000

export function orderAgeMinutes(placedAt: string, now: Date): number {
  const placed = Date.parse(placedAt)
  if (Number.isNaN(placed)) return 0

  const elapsed = now.getTime() - placed
  if (!Number.isFinite(elapsed) || elapsed <= 0) return 0

  return Math.floor(elapsed / MILLISECONDS_PER_MINUTE)
}

export function orderUrgency(status: string, ageMinutes: number): Urgency {
  const threshold = URGENCY_THRESHOLDS[status]
  if (!threshold) return "normal"
  if (ageMinutes >= threshold.late) return "late"
  if (ageMinutes >= threshold.warn) return "warn"
  return "normal"
}

export function statusActionsFor(order: Pick<BoardOrder, "status" | "serviceType">): OrderFlowAction[] {
  switch (order.status) {
    case "pending":
      return [
        { status: "in_progress", labelKey: `${ACTION_I18N_BASE}.start_preparation`, variant: "default" },
        { status: "cancelled", labelKey: `${ACTION_I18N_BASE}.cancel`, variant: "destructive" },
      ]
    case "in_progress":
      if (order.serviceType === "pick_up") {
        return [
          {
            status: "ready_for_pickup",
            labelKey: `${ACTION_I18N_BASE}.ready_for_pickup`,
            variant: "default",
            requiresEta: true,
          },
          { status: "cancelled", labelKey: `${ACTION_I18N_BASE}.cancel`, variant: "destructive" },
        ]
      }
      return [
        { status: "ready", labelKey: `${ACTION_I18N_BASE}.mark_ready`, variant: "default" },
        { status: "cancelled", labelKey: `${ACTION_I18N_BASE}.cancel`, variant: "destructive" },
      ]
    case "ready":
      return [{ status: "completed", labelKey: `${ACTION_I18N_BASE}.complete`, variant: "default" }]
    case "ready_for_pickup":
      return [
        { status: "picked_up", labelKey: `${ACTION_I18N_BASE}.picked_up`, variant: "default" },
        { status: "no_show", labelKey: `${ACTION_I18N_BASE}.no_show`, variant: "outline" },
      ]
    default:
      return []
  }
}

/**
 * The single verb a card carries. Destructive steps never qualify — losing an
 * order to a mis-tap on a moving board is not a recoverable mistake.
 */
export function primaryActionFor(
  order: Pick<BoardOrder, "status" | "serviceType">
): OrderFlowAction | null {
  return statusActionsFor(order).find((action) => action.variant === "default") ?? null
}

export function sortOldestFirst<T extends Pick<BoardOrder, "placedAt">>(orders: T[]): T[] {
  return [...orders].sort((left, right) => {
    const leftPlaced = Date.parse(left.placedAt)
    const rightPlaced = Date.parse(right.placedAt)
    const leftSortable = Number.isNaN(leftPlaced)
    const rightSortable = Number.isNaN(rightPlaced)

    if (leftSortable && rightSortable) return 0
    if (leftSortable) return 1
    if (rightSortable) return -1

    return leftPlaced - rightPlaced
  })
}

export function groupOrdersByStatus<T extends Pick<BoardOrder, "status">>(
  orders: T[],
  columnValues: string[]
): Record<string, T[]> {
  const grouped = columnValues.reduce<Record<string, T[]>>((accumulator, column) => {
    accumulator[column] = []
    return accumulator
  }, {})

  orders.forEach((order) => {
    grouped[order.status]?.push(order)
  })

  return grouped
}

export function stationOptionsFrom<T extends Pick<BoardOrder, "station" | "stationLabel">>(
  orders: T[]
): StationOption[] {
  const byValue = new Map<string, StationOption>()

  orders.forEach((order) => {
    if (!order.station || byValue.has(order.station)) return
    byValue.set(order.station, {
      value: order.station,
      label: order.stationLabel || order.station,
    })
  })

  return [...byValue.values()].sort((left, right) => left.label.localeCompare(right.label))
}

export function filterBoardOrders<T extends Pick<BoardOrder, "station" | "status" | "placedAt">>(
  orders: T[],
  { station, onlyDelayed, now }: { station: string; onlyDelayed: boolean; now: Date }
): T[] {
  return orders.filter((order) => {
    if (station !== "all" && order.station !== station) return false
    if (onlyDelayed && orderUrgency(order.status, orderAgeMinutes(order.placedAt, now)) === "normal") {
      return false
    }
    return true
  })
}
